import { useState, useEffect, useCallback } from 'react';
import { profileApi } from '../api.js';
import { getStorageItem, setStorageItem, STORAGE_KEYS } from '../utils/storage.js';

// 8 档年龄段（自由字符串，走中枢 facts 画像通道，无白名单校验；~18 起、48+ 封顶）
const AGE_RANGES = ['~18', '19-23', '24-28', '29-33', '34-38', '39-43', '44-47', '48+'];
const GENDERS = [
  { value: 'female', label: '女' },
  { value: 'male', label: '男' },
  { value: 'other', label: '其他' },
];

/**
 * 入口性别+年龄确认卡。
 * 走中枢【画像 facts】通道（/api/profile/facts，现成 GET/POST，key/value 自由存、upsert 覆盖）：
 *   读：profileApi.getFacts() 取 gender / age_range 两条 fact；
 *   写：profileApi.writeFacts([{gender},{age_range}]) 存进中枢画像。
 * 中枢零改动（无需改 AGE_RANGES / 加 GET 路由 / 部署）。
 *
 * 逻辑：进入 /report 先读 facts；gender 或 age_range 缺失 → 弹卡确认；确认后写 facts。
 * facts 已全填 → 静默放行。中枢不可达/未登录 → 不阻塞，本地 sessionStorage 兜底。
 * onReady({ gender, age_range, confirmed, hubOk }) 通知父组件（供 tier1/tier2 报告默认带性别）。
 */
export default function GenderConfirmCard({ onReady }) {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ gender: null, age_range: null });
  const [showCard, setShowCard] = useState(false);
  const [draft, setDraft] = useState({ gender: '', age_range: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const finish = useCallback((p, confirmed) => {
    setProfile(p);
    setLoading(false);
    const missing = !p.gender || !p.age_range;
    setShowCard(missing);
    onReady?.({ ...p, confirmed, hubOk: true });
  }, [onReady]);

  // 读中枢画像 facts
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let p = { gender: null, age_range: null };
      let hubOk = true;
      try {
        const facts = await profileApi.getFacts();
        const g = facts.find((f) => f.key === 'gender')?.value || null;
        const a = facts.find((f) => f.key === 'age_range')?.value || null;
        p = { gender: g, age_range: a };
        if (g && a) {
          await setStorageItem(STORAGE_KEYS.HUB_PROFILE, JSON.stringify(p));
        }
      } catch (e) {
        hubOk = false;
        // 中枢不可达 / 未登录：不阻塞，本地缓存兜底
        const cached = await getStorageItem(STORAGE_KEYS.HUB_PROFILE);
        if (cached) { try { p = JSON.parse(cached); } catch { /* keep empty */ } }
      }
      if (cancelled) return;
      if (hubOk || (p.gender && p.age_range)) {
        finish(p, false);
      } else {
        setLoading(false);
        setProfile(p);
        setShowCard(true);
        setError('暂未读到你的性别/年龄，请补全（保存后将同步到你的美妆画像）');
        onReady?.({ ...p, confirmed: false, hubOk: false });
      }
    })();
    return () => { cancelled = true; };
  }, [finish, onReady]);

  const pickGender = (g) => setDraft((d) => ({ ...d, gender: g }));
  const pickAge = (a) => setDraft((d) => ({ ...d, age_range: a }));

  const save = useCallback(async () => {
    if (!draft.gender || !draft.age_range) { setError('请选择性别和年龄段'); return; }
    setSaving(true); setError(null);
    try {
      // 写中枢画像 facts（gender / age_range 两条）
      await profileApi.writeFacts([
        { key: 'gender', value: draft.gender },
        { key: 'age_range', value: draft.age_range },
      ]);
      const p = { gender: draft.gender, age_range: draft.age_range };
      await setStorageItem(STORAGE_KEYS.HUB_PROFILE, JSON.stringify(p));
      setProfile(p);
      setShowCard(false);
      onReady?.({ ...p, confirmed: true, hubOk: true });
    } catch (e) {
      // 写中枢失败（不可达/未登录）：至少本地记住，让报告能默认带性别，不阻塞
      const p = { gender: draft.gender, age_range: draft.age_range };
      await setStorageItem(STORAGE_KEYS.HUB_PROFILE, JSON.stringify(p));
      setProfile(p);
      setShowCard(false);
      onReady?.({ ...p, confirmed: true, hubOk: false });
    } finally {
      setSaving(false);
    }
  }, [draft, onReady]);

  const close = useCallback(() => {
    const p = profile;
    setShowCard(false);
    onReady?.({ ...p, confirmed: false, hubOk: true });
  }, [profile, onReady]);

  if (loading) return null;

  return (
    <>
      {showCard && (
        <div className="gender-confirm-overlay" onClick={close}>
          <div className="gender-confirm-panel" onClick={(e) => e.stopPropagation()}>
            <div className="gender-confirm-head">
              <span className="gender-confirm-title">确认你的信息</span>
              <button className="gender-confirm-x" onClick={close} aria-label="稍后再说">✕</button>
            </div>
            <p className="gender-confirm-sub">用于为你定制专属美妆方案，保存后将同步到你的美妆画像</p>

            <div className="gender-confirm-group">
              <label className="gender-confirm-label">性别</label>
              <div className="gender-confirm-opts">
                {GENDERS.map((g) => (
                  <button
                    key={g.value}
                    type="button"
                    className={'gc-opt' + (draft.gender === g.value ? ' gc-opt--active' : '')}
                    onClick={() => pickGender(g.value)}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="gender-confirm-group">
              <label className="gender-confirm-label">年龄段</label>
              <div className="gender-confirm-opts">
                {AGE_RANGES.map((a) => (
                  <button
                    key={a}
                    type="button"
                    className={'gc-opt gc-opt--age' + (draft.age_range === a ? ' gc-opt--active' : '')}
                    onClick={() => pickAge(a)}
                  >
                    {a === '48+' ? '48岁及以上' : a === '~18' ? '18岁及以下' : a}
                  </button>
                ))}
              </div>
            </div>

            {error && <p className="gender-confirm-err">{error}</p>}
            <div className="gender-confirm-actions">
              <button
                type="button"
                className="gc-primary"
                disabled={saving || !draft.gender || !draft.age_range}
                onClick={save}
              >
                {saving ? '保存中…' : '确认并保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
